/**
 * Web coach-voice playback (Phase B). Fetches the auth'd audio Blob and plays it
 * via an <audio> element, reporting duration and cleaning up the object URL.
 * `stopCoachAudio` resolves the in-flight play promise so a barge-in ends cleanly
 * (a bare pause() fires no 'ended' event).
 */
import { practiceApi } from "@/api";
import type { PracticeVoice } from "@/types";

import { parseIntakeClipId } from "./coach-audio-id";
import { voiceClipKey } from "./practice-voice";

let current: HTMLAudioElement | null = null;
let resolveCurrent: (() => void) | null = null;

// In-memory, per-session cache of synthesized coach audio, keyed by messageId +
// selected voice. Once the backend delivers a clip we keep the bytes on the
// client, so replays cost NOTHING — no backend request, no TTS/OpenAI recall.
// Bounded LRU so a very long session can't grow memory without limit; fully
// cleared on leaving practice (background cleanup).
const audioCache = new Map<string, Blob>();
const MAX_CACHED_CLIPS = 50;

function rememberAudio(clipKey: string, blob: Blob): void {
  // delete+set refreshes recency (moves this clip to the newest slot).
  audioCache.delete(clipKey);
  audioCache.set(clipKey, blob);
  while (audioCache.size > MAX_CACHED_CLIPS) {
    const oldest = audioCache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    audioCache.delete(oldest);
  }
}

/** Drop every cached clip — call when leaving the practice screen. */
export function clearCoachAudioCache(): void {
  audioCache.clear();
}

export const COACH_AUDIO_SUPPORTED = true;

export interface PlayCoachOptions {
  onStart?: () => void;
  onDuration?: (ms: number) => void;
  /**
   * Accepted so the shared call site typechecks, and deliberately IGNORED.
   * The web build already fetches the clip into an in-memory blob cache and
   * has no equivalent of the native download-to-disk wait to remove.
   */
  live?: boolean;
}

export function stopCoachAudio(): void {
  if (current) {
    current.pause();
    current = null;
  }
  if (resolveCurrent) {
    resolveCurrent();
    resolveCurrent = null;
  }
}

export async function playCoachMessage(
  messageId: string,
  voice: PracticeVoice,
  opts?: PlayCoachOptions,
): Promise<void> {
  // Serve from the client cache when we already have this clip; otherwise fetch
  // once (may 503 → caller fails closed) and keep it for future replays.
  const clipKey = voiceClipKey(messageId, voice);
  let blob = audioCache.get(clipKey);
  if (!blob) {
    // A BYO intake guide turn fetches from the intake-audio endpoint; everything
    // else is a stored coach message.
    const intake = parseIntakeClipId(messageId);
    blob = intake
      ? await practiceApi.intakeAudio(intake.sessionId, intake.index, voice)
      : await practiceApi.messageAudio(messageId, voice);
  }
  rememberAudio(clipKey, blob);
  stopCoachAudio();
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  current = audio;
  audio.onloadedmetadata = () => {
    if (opts?.onDuration && Number.isFinite(audio.duration)) {
      opts.onDuration(Math.round(audio.duration * 1000));
    }
  };
  try {
    await audio.play();
    opts?.onStart?.();
    await new Promise<void>((resolve) => {
      resolveCurrent = resolve;
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
    });
  } finally {
    URL.revokeObjectURL(url);
    if (current === audio) current = null;
    resolveCurrent = null;
  }
}
